import apiClient from './api';
import API_CONFIG from '../config/api';

// --- Helpers: safe variant + attribute preservation for update_product ---
const buildCompleteVariantsWithAttributes = (product) => {
  const variants = product?.variants || [];
  const variantAttributes = product?.variant_attributes || [];
  // attach attributes to each variant
  return variants.map(v => {
    const attrs = variantAttributes
      .filter(a => a.variant_id === v.id)
      .map(a => ({
        attribute_name: a.attribute_name,
        attribute_value: a.attribute_value,
        attribute_id: a.attribute_id ?? null,
        attribute_value_id: a.attribute_value_id ?? null
      }));
    return {
      ...v,
      attributes: attrs
    };
  });
};

// Merge rule: update by sku (or id fallback), preserve existing fields when not provided
const mergeVariantsSafely = (existing = [], updates = []) => {
  const byKey = (v) => (v?.sku ? `sku:${v.sku}` : (v?.id ? `id:${v.id}` : null));
  const map = new Map();
  existing.forEach(v => {
    const k = byKey(v);
    if (k) map.set(k, { ...v });
  });
  updates.forEach(u => {
    const k = byKey(u);
    if (k && map.has(k)) {
      const prev = map.get(k);
      map.set(k, {
        ...prev,
        ...u,
        id: prev.id ?? u.id, // preserve id
        attributes: Array.isArray(u.attributes) ? u.attributes : (prev.attributes || [])
      });
    } else if (k) {
      map.set(k, { ...u });
    } else {
      // no key -> push as-is but avoid duplicates
      map.set(`tmp:${Math.random()}`, { ...u });
    }
  });
  return Array.from(map.values());
};

export const authService = {
  // Test connection
  testConnection: async () => {
    try {
      const response = await apiClient.get('/api/health');
      ////console.log('Health check response:', response.data);
      return response.data;
    } catch (error) {
      ////console.error('Health check failed:', error);
      throw error;
    }
  },

  // Đăng nhập
  login: async (username, password, deviceInfo = 'Admin Portal') => {
    const response = await apiClient.post('/api/users/login', {
      username,
      password,
      deviceInfo,
    });
    return response.data;
  },

  // Đăng xuất
  logout: async () => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      await apiClient.post('/api/users/logout', { accessToken });
    }
    localStorage.removeItem('accessToken');
    localStorage.removeItem('userInfo');
  },

  // Làm mới token
  refreshToken: async () => {
    const refreshToken = localStorage.getItem('refreshToken');
    const response = await apiClient.post('/api/auth/refresh-token', {
      refreshToken,
    });
    return response.data;
  },

  // Đăng ký
  register: async (userData) => {
    const response = await apiClient.post('/api/users/register', userData);
    return response.data;
  },
};

export const categoryService = {
  // Lấy tất cả phân loại
  getAll: async () => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken
    };
    const response = await apiClient.get('/api/categories/', { params: queryParams });
    return response.data;
  },

  // Lấy thông tin phân loại
  getById: async (categoryId) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken
    };
    const response = await apiClient.get(`/api/categories/${categoryId}`, { params: queryParams });
    return response.data;
  },

  // Thêm phân loại
  create: async (categoryData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/categories/add', {
      accessToken,
      ...categoryData,
    });
    return response.data;
  },

  // Sửa phân loại
  update: async (categoryId, categoryData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/api/categories/${categoryId}`, {
      accessToken,
      ...categoryData,
    });
    return response.data;
  },

  // Xóa phân loại
  delete: async (categoryId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/api/categories/${categoryId}`, {
      data: { accessToken },
    });
    return response.data;
  },
};

export const productService = {
  // Tìm kiếm sản phẩm nâng cao (theo API documentation)
  searchAdvanced: async (params = {}) => {
    const queryParams = {
      page: params.page || 1,
      pageSize: params.pageSize || 20,
      keyword: params.keyword || '',
      categoryId: params.categoryId || null,
      priceMin: params.priceMin || null,
      priceMax: params.priceMax || null,
      attributeValueIds: params.attributeValueIds ? JSON.stringify(params.attributeValueIds) : null,
      sortBy: params.sortBy || 'newest',
      sortDir: params.sortDir || 'desc'
    };
    
    // Loại bỏ các tham số null
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] === null || queryParams[key] === '') {
        delete queryParams[key];
      }
    });
    
    const response = await apiClient.get('/api/products/search/advanced', { params: queryParams });
    return response.data;
  },

  // Lấy danh sách sản phẩm
  getAll: async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      ...params
    };
    const response = await apiClient.get('/api/products/', { params: queryParams });
    return response.data;
  },

  // Lấy danh sách sản phẩm đầy đủ
  getFull: async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      ...params
    };
    const response = await apiClient.get('/api/products/full', { params: queryParams });
    return response.data;
  },

  // Lấy chi tiết sản phẩm
  getById: async (productId) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken
    };
    const response = await apiClient.get(`/api/products/${productId}`, { params: queryParams });
    return response.data;
  },

  // Lấy lịch sử tồn kho sản phẩm
  getStockHistory: async (productId, params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      ...params
    };
    const response = await apiClient.get(`/api/products/${productId}/stock-history`, { params: queryParams });
    return response.data;
  },

  // Lấy sản phẩm nổi bật
  getFeatured: async (type = 'featured') => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      type
    };
    const response = await apiClient.get(`/api/products/featured`, { params: queryParams });
    return response.data;
  },

  // Thêm sản phẩm từ JSON
  create: async (productData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/products/add-json', {
      accessToken,
      productData,
    });
    return response.data;
  },

  // Sửa sản phẩm
  update: async (productId, productData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/api/products/${productId}`, {
      accessToken,
      productData,
    });
    return response.data;
  },

  // Cập nhật sản phẩm đơn giản (không ảnh hưởng variants)
  updateSimple: async (productId, productData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/api/products/${productId}/simple`, {
      accessToken,
      productData,
    });
    return response.data;
  },

  // Sửa sản phẩm AN TOÀN (luôn gửi đầy đủ variants + attributes)
  // Dùng cho procedure update_product (REPLACE ALL variants)
  updateSafe: async (productId, productData) => {
    const accessToken = localStorage.getItem('accessToken');
    // Debug: In product_data received from UI
    try {
      ////console.log('🔎 updateSafe() input productData:', productData);
    } catch (_) {}

    // 1) Luôn lấy sản phẩm hiện có để bảo toàn variants/attributes
    const existingRes = await apiClient.get(`/api/products/${productId}`, {
      params: { accessToken }
    });
    const existing = existingRes?.data?.data || {};
    const completeExistingVariants = buildCompleteVariantsWithAttributes(existing);
    const existingVariantAttributes = Array.isArray(existing?.variant_attributes) ? existing.variant_attributes : [];
    // Map attribute_value_id -> attribute_id (từ dữ liệu hiện có)
    const valueIdToAttrId = new Map();
    existingVariantAttributes.forEach(a => {
      if (a && a.attribute_value_id && a.attribute_id) {
        valueIdToAttrId.set(a.attribute_value_id, a.attribute_id);
      }
    });

    // 2) Tạo danh sách variants an toàn để gửi
    // Nếu UI gửi danh sách variants -> coi là danh sách CHUẨN cuối cùng (REPLACE ALL)
    // - Với mỗi variant được gửi: tự động điền id và attributes nếu UI không gửi
    // - Các variant không có trong danh sách sẽ bị XÓA (không đưa vào payload)
    let safeVariants = completeExistingVariants;
    if (Array.isArray(productData?.variants)) {
      const updatedVariants = productData.variants;
      const findMatch = (u) => {
        if (u?.sku) return completeExistingVariants.find(ev => ev?.sku && ev.sku === u.sku);
        if (u?.id) return completeExistingVariants.find(ev => ev?.id && ev.id === u.id);
        return undefined;
      };
      safeVariants = updatedVariants.map(u => {
        const prev = findMatch(u) || {};
        const id = prev.id ?? u.id ?? null;
        // Xác định attributes cho biến thể: ưu tiên u.attributes, sau đó prev.attributes, cuối cùng xây từ attribute_value_ids
        let attrs = Array.isArray(u.attributes) ? u.attributes : (prev.attributes || []);
        if ((!attrs || attrs.length === 0) && Array.isArray(u.attribute_value_ids)) {
          attrs = u.attribute_value_ids.map(vId => ({
            attribute_id: valueIdToAttrId.get(vId) ?? null,
            attribute_value_id: vId
          }));
        }
        // Đồng bộ attribute_value_ids nếu không được cung cấp
        const attribute_value_ids = Array.isArray(u.attribute_value_ids)
          ? u.attribute_value_ids
          : (Array.isArray(attrs) ? attrs.map(a => a.attribute_value_id).filter(Boolean) : []);
        return {
          ...prev, // preserve previous fields like sku, etc.
          ...u,
          id,
          attributes: attrs,
          attribute_value_ids,
        };
      });
    }

    // 3) Suy luận attributes cấp sản phẩm nếu không được cung cấp
    let finalAttributes = Array.isArray(productData?.attributes) ? productData.attributes : null;
    if (!finalAttributes || finalAttributes.length === 0) {
      const attrIdSet = new Set();
      (safeVariants || []).forEach(v => {
        (v?.attributes || []).forEach(a => {
          if (a && a.attribute_id) attrIdSet.add(a.attribute_id);
        });
      });
      if (attrIdSet.size > 0) {
        finalAttributes = Array.from(attrIdSet).map(id => ({ id }));
      } else {
        finalAttributes = [];
      }
    }

    // Debug: liệt kê giá trị thuộc tính theo từng biến thể
    try {
      ////console.groupCollapsed('🧩 updateSafe() variants attribute debug');
      (safeVariants || []).forEach((v, idx) => {
        const info = {
          idx,
          id: v?.id ?? null,
          sku: v?.sku ?? null,
          attribute_value_ids: Array.isArray(v?.attribute_value_ids) ? v.attribute_value_ids : [],
          attributes: Array.isArray(v?.attributes) ? v.attributes : []
        };
        ////console.log('• Variant:', info);
        if (!info.attribute_value_ids.length) {
          ////console.warn('⚠️ Missing attribute_value_ids for variant:', info.sku || info.id);
        }
      });
      ////console.groupEnd();
    } catch (_) {}

    // 4) Gộp payload cuối cùng: luôn bao gồm full variants và attributes (nếu có)
    const finalPayload = {
      ...productData,
      variants: safeVariants,
      attributes: finalAttributes
    };

    // 5) Gọi API với payload an toàn
    try {
      ////console.log('🧾 updateSafe() finalPayload to send:', finalPayload);
    } catch (_) {}
    const response = await apiClient.put(`/api/products/${productId}`, {
      accessToken,
      productData: finalPayload,
    });
    return response.data;
  },

  // Xóa sản phẩm
  delete: async (productId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/api/products/${productId}`, {
      data: { accessToken },
    });
    return response.data;
  },

  // Phục hồi sản phẩm
  restore: async (productId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post(`/api/products/${productId}/restore`, {
      accessToken,
    });
    return response.data;
  },

  // Đổi trạng thái kích hoạt
  // activeStatus: boolean | number (1 = active, 0 = inactive)
  toggleActive: async (productId, activeStatus) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/api/products/${productId}/toggle-active`, {
      accessToken,
      activeStatus: activeStatus ? 1 : 0,
    });
    return response.data;
  },

  // Cập nhật nhãn sản phẩm
  updateFlags: async (productId, requestBody) => {
    const response = await apiClient.put(`/api/products/${productId}/flags`, requestBody);
    return response.data;
  },

  // Cập nhật khuyến mãi
  updatePromotion: async (productId, promotionData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/api/products/${productId}/promotion`, {
      accessToken,
      ...promotionData,
    });
    return response.data;
  },

  // === VARIANT MANAGEMENT APIs ===
  // Thêm biến thể mới
  addVariant: async (productId, variantData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post(
      `/api/products/${productId}/variants`,
      {
        accessToken,
        variantData
      }
    );
    return response.data;
  },

  // Cập nhật biến thể
  updateVariant: async (variantId, variantData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(
      `/api/products/variants/${variantId}`,
      {
        accessToken,
        variantData
      }
    );
    return response.data;
  },

  // Xóa biến thể
  deleteVariant: async (variantId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.delete(
      `/api/products/variants/${variantId}`,
      {
        data: { accessToken }
      }
    );
    return response.data;
  },
};

export const invoiceService = {
  // Hóa đơn xuất
  export: {
    getAll: async (params = {}) => {
      const accessToken = localStorage.getItem('accessToken');
      const queryParams = {
        accessToken,
        keyword: params.keyword || '', // Mặc định empty string thay vì null
        statusId: params.statusId || 0, // Mặc định 0 (tất cả) thay vì null  
        dateFrom: params.dateFrom || null, // Có thể null
        dateTo: params.dateTo || null, // Có thể null
        offset: params.offset || 0,
        limit: params.limit || 20
      };
      
      const response = await apiClient.get('/api/export/list', { params: queryParams });
      return response.data;
    },

    getById: async (invoiceId) => {
      const accessToken = localStorage.getItem('accessToken');
      const queryParams = {
        accessToken
      };
      const response = await apiClient.get(`/api/export/detail/${invoiceId}`, { params: queryParams });
      return response.data;
    },

    create: async (invoiceData) => {
      const accessToken = localStorage.getItem('accessToken');
      const response = await apiClient.post('/api/export/create-with-details', {
        accessToken,
        invoiceData: invoiceData,
      });
      return response.data;
    },

    createByUserId: async (invoiceData) => {
      const accessToken = localStorage.getItem('accessToken');
      const response = await apiClient.post('/api/export/create-by-user-id', {
        accessToken,
        invoiceData: invoiceData,
      });
      return response.data;
    },

    // Tạo hóa đơn xuất cho quản lý (Admin/Manager only)
    createForManagement: async (invoiceData) => {
      const accessToken = localStorage.getItem('accessToken');
      const response = await apiClient.post('/api/auth/create-export-invoice-for-management', {
        accessToken,
        ...invoiceData,
      });
      return response.data;
    },

    addDetail: async (detailData) => {
      const accessToken = localStorage.getItem('accessToken');
      const response = await apiClient.post('/api/export/add-detail', {
        accessToken,
        ...detailData,
      });
      return response.data;
    },

    updateStatus: async (invoiceId, statusData) => {
      const accessToken = localStorage.getItem('accessToken');
      const userId = localStorage.getItem('userId');
      const response = await apiClient.put('/api/export/update-status', {
        accessToken,
        userId: userId,
        invoiceId: invoiceId,
        statusId: statusData.statusId,
        note: statusData.note || ''
      });
      return response.data;
    },

    getStatusHistory: async (invoiceId) => {
      const accessToken = localStorage.getItem('accessToken');
      const queryParams = {
        accessToken
      };
      const response = await apiClient.get(`/api/export/status-history/${invoiceId}`, { params: queryParams });
      return response.data;
    },

    // Lấy danh sách trạng thái hóa đơn
    getStatuses: async () => {
      const response = await apiClient.get('/api/export/statuses');
      return response.data;
    },

    // Lấy danh sách hóa đơn cần tạo vận đơn
    getShippingList: async () => {
      const accessToken = localStorage.getItem('accessToken');
      const response = await apiClient.get('/api/export/shipping-list', {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });
      return response.data;
    },
  },

  // Hóa đơn nhập
  import: {
    getAll: async (params = {}) => {
      const accessToken = localStorage.getItem('accessToken');
      
      // Debug: Check if token exists
      if (!accessToken) {
        ////console.error('❌ AccessToken not found in localStorage');
        throw new Error('AccessToken not found. Please login again.');
      }
      
      ////console.log('🔑 Using accessToken:', accessToken.substring(0, 20) + '...');
      
      // Map frontend params to API params - only send non-empty values
      const queryParams = {
        accessToken,
        page: params.offset ? Math.floor(params.offset / (params.limit || 20)) + 1 : 1,
        pageSize: params.limit || 20,
      };
      
      // Only add filters if they have values - use exact API values
      if (params.keyword) queryParams.codeFilter = params.keyword.substring(0, 50);
      if (params.supplierKeyword) queryParams.supplierFilter = params.supplierKeyword.substring(0, 50);
      if (params.status) queryParams.statusFilter = params.status; // Don't truncate - API needs exact values
      if (params.dateFrom) queryParams.dateFrom = params.dateFrom;
      if (params.dateTo) queryParams.dateTo = params.dateTo;
      
      ////console.log('📤 API Request URL:', '/api/invoices/import');
      ////console.log('📤 API Request params:', queryParams);
      
      const response = await apiClient.get('/api/invoices/import', { params: queryParams });
      return response.data;
    },

    getById: async (invoiceId) => {
      const accessToken = localStorage.getItem('accessToken');
      const queryParams = {
        accessToken
      };
      const response = await apiClient.get(`/api/invoices/import/${invoiceId}`, { params: queryParams });
      return response.data;
    },

    create: async (invoiceData) => {
      const accessToken = localStorage.getItem('accessToken');
      const response = await apiClient.post('/api/invoices/import', {
        accessToken,
        ...invoiceData,
      });
      return response.data;
    },

    // New improved API - create invoice with details in one call
    createWithDetails: async (invoiceData) => {
      const accessToken = localStorage.getItem('accessToken');
      const response = await apiClient.post('/api/invoices/import/create-with-details', {
        accessToken,
        ...invoiceData,
      });
      return response.data;
    },

    update: async (invoiceId, invoiceData) => {
      const accessToken = localStorage.getItem('accessToken');
      const response = await apiClient.put(`/api/invoices/import/${invoiceId}`, {
        accessToken,
        ...invoiceData,
      });
      return response.data;
    },

    // Update import invoice with details in one call (NEW)
    updateWithDetails: async (invoiceId, invoiceData) => {
      const accessToken = localStorage.getItem('accessToken');
      // Use config helper function
      const url = API_CONFIG.getApiUrl(`/api/invoices/import/${invoiceId}/update-with-details`);
      const response = await apiClient.put(url, {
        accessToken,
        ...invoiceData,
      });
      return response.data;
    },

    // Approve import invoice (Chưa giải quyết → hoàn thành)
    approve: async (invoiceId, note = '') => {
      const accessToken = localStorage.getItem('accessToken');
      const url = API_CONFIG.getApiUrl(`/api/invoices/import/${invoiceId}/approve`);
      const response = await apiClient.post(url, {
        accessToken,
        note,
      });
      return response.data;
    },

    // Cancel import invoice (Chưa giải quyết → Đã hủy bỏ)
    cancel: async (invoiceId, note = '') => {
      const accessToken = localStorage.getItem('accessToken');
      const url = API_CONFIG.getApiUrl(`/api/invoices/import/${invoiceId}/cancel`);
      const response = await apiClient.post(url, {
        accessToken,
        note,
      });
      return response.data;
    },

    // Cancel completed import invoice (hoàn thành → Đã hủy bỏ)
    cancelCompleted: async (invoiceId, note = '') => {
      const accessToken = localStorage.getItem('accessToken');
      const url = API_CONFIG.getApiUrl(`/api/invoices/import/${invoiceId}/cancel-completed`);
      const response = await apiClient.post(url, {
        accessToken,
        note,
      });
      return response.data;
    },

    delete: async (invoiceId) => {
      const accessToken = localStorage.getItem('accessToken');
      const response = await apiClient.delete(`/api/invoices/import/${invoiceId}`, {
        data: { accessToken },
      });
      return response.data;
    },

    addDetail: async (detailData) => {
      const accessToken = localStorage.getItem('accessToken');
      const response = await apiClient.post('/api/invoices/import/detail', {
        accessToken,
        ...detailData,
      });
      return response.data;
    },

    // Approve a pending import invoice (updates stock)
    approve: async (invoiceId, note = '') => {
      const accessToken = localStorage.getItem('accessToken');
      const url = API_CONFIG.getApiUrl(`/api/invoices/import/${invoiceId}/approve`);
      const response = await apiClient.post(url, { accessToken, note });
      return response.data;
    },

    // Cancel a pending import invoice (no stock change)
    cancel: async (invoiceId, note = '') => {
      const accessToken = localStorage.getItem('accessToken');
      const url = API_CONFIG.getApiUrl(`/api/invoices/import/${invoiceId}/cancel`);
      const response = await apiClient.post(url, { accessToken, note });
      return response.data;
    },

    // Cancel a completed import invoice (revert stock)
    cancelCompleted: async (invoiceId, note = '') => {
      const accessToken = localStorage.getItem('accessToken');
      const url = API_CONFIG.getApiUrl(`/api/invoices/import/${invoiceId}/cancel-completed`);
      const response = await apiClient.post(url, { accessToken, note });
      return response.data;
    },
  },
};

export const stockService = {
  // Lấy danh sách variants tồn kho
  getVariants: async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      ...params
    };
    const response = await apiClient.get('/api/stock/variants', { params: queryParams });
    return response.data;
  },

  // Lấy lịch sử thay đổi kho
  getHistory: async (variantId, params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      page: params.page || 1,
      pageSize: params.pageSize || 10,
      ...params
    };
    ////console.log('stockService.getHistory - variantId:', variantId);
    ////console.log('stockService.getHistory - queryParams:', queryParams);
    const url = `/api/stock/${variantId}/history`;
    ////console.log('stockService.getHistory - URL:', url);
    const response = await apiClient.get(url, { params: queryParams });
    ////console.log('stockService.getHistory - response:', response);
    return response.data;
  },

  // Thay đổi tồn kho
  update: async (variantId, stockData) => {
    ////console.log('stockService.update - variantId:', variantId);
    ////console.log('stockService.update - stockData:', stockData);
    const url = `/api/stock/${variantId}`;
    ////console.log('stockService.update - URL:', url);
    
    // Thêm accessToken vào body của request
    const accessToken = localStorage.getItem('accessToken');
    const requestBody = {
      accessToken,
      ...stockData
    };
    ////console.log('stockService.update - requestBody:', requestBody);
    
    const response = await apiClient.put(url, requestBody);
    ////console.log('stockService.update - response:', response);
    return response.data;
  },
};

export const customerService = {
  // Lấy danh sách khách hàng
  getAll: async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      keyword: params.keyword || '',
      offset: params.offset || 0,
      limit: params.limit || 100,
    };
    const response = await apiClient.get('/api/customers/', { params: queryParams });
    return response.data;
  },

  // Lấy danh sách địa chỉ khách hàng
  getAddresses: async (customerId) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = { accessToken };
    const response = await apiClient.get(`/api/customers/${customerId}/addresses`, { params: queryParams });
    return response.data;
  },

  // Lấy lịch sử đơn hàng
  getOrderHistory: async (customerId, params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      ...params
    };
    const response = await apiClient.get(`/api/customers/${customerId}/order-history`, { params: queryParams });
    return response.data;
  },

  // Báo cáo khách hàng
  getReport: async (customerId, params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      ...params
    };
    const response = await apiClient.get(`/api/customers/${customerId}/report`, { params: queryParams });
    return response.data;
  },
};

export const newsService = {
  // ===== NEWS APIs =====
  // Lấy danh sách tin tức (Public API)
  getAll: async (params = {}) => {
    const response = await apiClient.get('/api/news', { params });
    return response.data;
  },

  // Lấy chi tiết tin tức (Public API)
  getById: async (newsId) => {
    const response = await apiClient.get(`/api/news/${newsId}`);
    return response.data;
  },

  // Thêm tin tức (Admin/Manager)
  create: async (newsData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/news/add', {
      accessToken,
      ...newsData,
    });
    return response.data;
  },

  // Sửa tin tức (Admin/Manager)
  update: async (newsId, newsData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/api/news/${newsId}`, {
      accessToken,
      ...newsData,
    });
    return response.data;
  },

  // Xóa tin tức (Admin/Manager)
  delete: async (newsId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/api/news/${newsId}`, {
      data: { accessToken },
    });
    return response.data;
  },

  // ===== NEWS TOPICS APIs =====
  // Lấy danh sách chủ đề bản tin (Public API - không cần token)
  getTopics: async () => {
    const response = await apiClient.get('/api/news/topics');
    return response.data;
  },

  // Thêm chủ đề bản tin (Admin/Manager)
  createTopic: async (topicData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/news/topics', topicData, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  // Sửa chủ đề bản tin (Admin/Manager)
  updateTopic: async (topicId, topicData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/api/news/topics/${topicId}`, topicData, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  // Xóa chủ đề bản tin (Admin/Manager)
  deleteTopic: async (topicId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/api/news/topics/${topicId}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    return response.data;
  },

  // ===== NEWS CATEGORIES APIs =====
  // Lấy danh sách danh mục bản tin (Public API - không cần token)
  getCategories: async () => {
    const response = await apiClient.get('/api/news/categories');
    return response.data;
  },

  // Thêm danh mục bản tin (Admin/Manager)
  createCategory: async (categoryData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/news/categories', categoryData, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  // Sửa danh mục bản tin (Admin/Manager)
  updateCategory: async (categoryId, categoryData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/api/news/categories/${categoryId}`, categoryData, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  // Xóa danh mục bản tin (Admin/Manager)
  deleteCategory: async (categoryId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/api/news/categories/${categoryId}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    return response.data;
  },
};

export const reportService = {
  // Báo cáo sản phẩm bán chạy
  getBestSellingProducts: async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      ...params
    };
    const response = await apiClient.get('/api/reports/best-selling-products', { params: queryParams });
    return response.data;
  },

  // Báo cáo tồn kho thấp
  getLowStockProducts: async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      ...params
    };
    const response = await apiClient.get('/api/reports/low-stock-products', { params: queryParams });
    return response.data;
  },

  // Báo cáo tổng hợp trạng thái đơn
  getOrderStatusSummary: async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      ...params
    };
    const response = await apiClient.get('/api/reports/order-status-summary', { params: queryParams });
    return response.data;
  },

  // Báo cáo doanh thu
  getTotalRevenue: async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      ...params
    };
    const response = await apiClient.get('/api/reports/total-revenue', { params: queryParams });
    return response.data;
  },
};

export const paymentService = {
  // Tạo QR thanh toán
  generateQR: async (paymentData) => {
    const response = await apiClient.post('/api/payment/generate-qr', paymentData);
    return response.data;
  },
};

export const attributeService = {
  // Lấy tất cả thuộc tính (kể cả không có giá trị) - cho danh sách admin
  getAll: async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const queryParams = {
        accessToken
      };
      const response = await apiClient.get('/api/attributes/all', { params: queryParams });
      return response.data;
    } catch (error) {
      ////console.error('Error in attributeService.getAll:', error);
      throw error;
    }
  },

  // Lấy tất cả thuộc tính với giá trị - cho form sản phẩm
  getAllWithValues: async () => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const queryParams = {
        accessToken
      };
      const response = await apiClient.get('/api/attributes/', { params: queryParams });
      return response.data;
    } catch (error) {
      ////console.error('Error in attributeService.getAllWithValues:', error);
      throw error;
    }
  },

  // Lấy thuộc tính theo ID
  getById: async (attributeId) => {
    try {
      const response = await apiClient.get(`/api/attributes/${attributeId}`);
      return response.data;
    } catch (error) {
      ////console.error('Error in attributeService.getById:', error);
      throw error;
    }
  }
  ,

  // Thêm thuộc tính
  create: async (attributeData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/attributes', { accessToken, ...attributeData });
    return response.data;
  },

  // Sửa thuộc tính
  update: async (attributeId, attributeData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/api/attributes/${attributeId}`, { accessToken, ...attributeData });
    return response.data;
  },

  // Xóa thuộc tính
  delete: async (attributeId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/api/attributes/${attributeId}`, { data: { accessToken } });
    return response.data;
  },

  // Thêm giá trị thuộc tính
  addValue: async (attributeId, value) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post(`/api/attributes/${attributeId}/values`, { accessToken, attribute_id: attributeId, value });
    return response.data;
  },

  // Sửa giá trị thuộc tính
  updateValue: async (valueId, value) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/api/attributes/values/${valueId}`, { accessToken, value });
    return response.data;
  },

  // Xóa giá trị thuộc tính
  deleteValue: async (valueId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/api/attributes/values/${valueId}`, { data: { accessToken } });
    return response.data;
  },
};

export const userService = {
  // Lấy danh sách người dùng với bộ lọc và phân trang
  getList: async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      keyword: params.keyword || '',
      roleFilter: params.roleFilter || null,
      statusFilter: params.statusFilter || null,
      offset: params.offset || 0,
      limit: params.limit || 20,
    };

    // Remove null values from query params
    Object.keys(queryParams).forEach(key => {
      if (queryParams[key] === null) {
        delete queryParams[key];
      }
    });

    const response = await apiClient.get('/api/users/list', { params: queryParams });
    return response.data;
  },

  // Lấy thông tin chi tiết người dùng
  getById: async (userId) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken
    };
    const response = await apiClient.get(`/api/users/${userId}`, { params: queryParams });
    return response.data;
  },

  // Tạo người dùng mới
  create: async (userData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/users/create', {
      accessToken,
      ...userData,
    });
    return response.data;
  },

  // Cập nhật thông tin cơ bản người dùng
  // Cập nhật thông tin người dùng
  update: async (userId, userData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/api/users/${userId}`, {
      accessToken,
      ...userData,
    });
    return response.data;
  },

  // Xóa người dùng
  delete: async (userId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/api/users/${userId}`, {
      data: { accessToken },
    });
    return response.data;
  },

  // Thay đổi trạng thái người dùng (active/inactive/banned)
  updateStatus: async (targetUserId, newStatus) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put('/api/users/update-status', {
      accessToken,
      targetUserId,
      newStatus,
    });
    return response.data;
  },

  // Thay đổi mật khẩu
  changePassword: async (userId, passwordData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/api/users/${userId}/password`, {
      accessToken,
      ...passwordData,
    });
    return response.data;
  },

  // Thay đổi vai trò người dùng
  updateRole: async (targetUserId, newRole) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put('/api/users/update-role', {
      accessToken,
      targetUserId,
      newRole,
    });
    return response.data;
  },

  // Lấy thông tin chi tiết người dùng
  getDetail: async (userId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.get(`/api/users/${userId}/detail`, {
      params: { accessToken }
    });
    return response.data;
  },

  // Lấy danh sách địa chỉ người dùng
  getAddresses: async (userId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.get(`/api/users/${userId}/addresses`, {
      params: { accessToken }
    });
    return response.data;
  },

  // Lấy thống kê đơn hàng người dùng
  getOrderStats: async (userId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.get(`/api/users/${userId}/order-stats`, {
      params: { accessToken }
    });
    return response.data;
  },

  // Lấy danh sách đơn hàng người dùng với phân trang
  getOrders: async (userId, params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      offset: params.offset || 0,
      limit: params.limit || 20,
    };

    const response = await apiClient.get(`/api/users/${userId}/orders`, {
      params: queryParams
    });
    return response.data;
  },

  // Lấy báo cáo dashboard
  getDashboardReport: async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
    };
    
    // Thêm các tham số ngày nếu có
    if (params.dateFrom) queryParams.dateFrom = params.dateFrom;
    if (params.dateTo) queryParams.dateTo = params.dateTo;
    
    const response = await apiClient.get('/api/users/dashboard-report', {
      params: queryParams
    });
    return response.data;
  },

  // Thêm người dùng mới
  addUser: async (userData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/users/add-user', {
      accessToken,
      ...userData,
    });
    return response.data;
  },

  // ===== NHÂN SỰ CẦN DUYỆT APIs =====
  // Lấy danh sách nhóm đại lý chưa duyệt
  getPendingNhomdaily: async () => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/auth/get-pending-nhomdaily', {
      accessToken,
    });
    return response.data;
  },

  // Duyệt nhân viên vào nhóm đại lý
  approveEmployee: async (userId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/auth/approve-employee', {
      accessToken,
      IDUsers: userId,
    });
    return response.data;
  },

  // Hủy duyệt nhân viên
  rejectEmployee: async (userId) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/auth/reject-employee', {
      accessToken,
      IDUsers: userId,
    });
    return response.data;
  },
  
  // Lấy thông tin bophankinhdoanh id=1
  getBophankinhdoanh: async () => {
    const accessToken = localStorage.getItem('accessToken');
    // This endpoint does not require accessToken but include for consistency
    const response = await apiClient.post('/api/auth/get-bophankinhdoanh', {});
    return response.data;
  },

  // Cập nhật bophankinhdoanh bằng token (admin/manager)
  updateBophankinhdoanh: async (payload = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const requestData = {
      accessToken,
      ...payload
    };
    const response = await apiClient.post('/api/auth/update-bophankinhdoanh', requestData);
    return response.data;
  },
  
  // Lấy tổng điểm thưởng và danh sách chi phí->điểm thưởng (manager only)
  getDiemthuongChiphi: async () => {
    const accessToken = localStorage.getItem('accessToken');
    const requestData = {
      accessToken
    };
    const response = await apiClient.post('/api/auth/get-diemthuong-chiphi', requestData);
    return response.data;
  },


};

// Business Department Service (Bộ phận Kinh Doanh)
export const bophanService = {
  // Get department info (id=1)
  getBophankinhdoanh: async () => {
    const response = await apiClient.post('/api/auth/get-bophankinhdoanh', {});
    return response.data;
  },

  // Update department info by token (admin/manager)
  updateBophankinhdoanh: async (payload = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const requestData = {
      accessToken,
      ...payload
    };
    const response = await apiClient.post('/api/auth/update-bophankinhdoanh', requestData);
    return response.data;
  }
};

// ViettelPost Shipping Service
export const shippingService = {
  // Tạo vận đơn ViettelPost
  createOrder: async (orderCode) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/viettelpost/create-order', 
      { order_code: orderCode },
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      }
    );
    return response.data;
  },

  // Lấy giá vận chuyển chi tiết
  getPrices: async (shippingData) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await apiClient.post('/api/shipping/get-prices', {
        accessToken,
        ...shippingData,
      });
      return response.data;
    } catch (error) {
      ////console.error('🚚 Shipping getPrices error:', error);
      throw error;
    }
  },

  // Lấy giá vận chuyển đơn giản (rẻ nhất & nhanh nhất)
  getSimplePrices: async (shippingData) => {
    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await apiClient.post('/api/shipping/get-simple-prices', {
        accessToken,
        ...shippingData,
      });
      return response.data;
    } catch (error) {
      ////console.error('🚚 Shipping getSimplePrices error:', error);
      throw error;
    }
  },

  // Lấy danh sách tỉnh/thành phố
  getProvinces: async () => {
    try {
      const response = await fetch('https://partner.viettelpost.vn/v2/categories/listProvinceById?provinceId=');
      const data = await response.json();
      return { success: true, data: data.data || [] };
    } catch (error) {
      ////console.error('🏙️ Get provinces error:', error);
      throw error;
    }
  },

  // Lấy danh sách quận/huyện theo tỉnh
  getDistricts: async (provinceId) => {
    try {
      const response = await fetch(`https://partner.viettelpost.vn/v2/categories/listDistrict?provinceId=${provinceId}`);
      const data = await response.json();
      return { success: true, data: data.data || [] };
    } catch (error) {
      ////console.error('🏘️ Get districts error:', error);
      throw error;
    }
  },

  // Lấy danh sách xã/phường theo quận
  getWards: async (districtId) => {
    try {
      const response = await fetch(`https://partner.viettelpost.vn/v2/categories/listWards?districtId=${districtId}`);
      const data = await response.json();
      return { success: true, data: data.data || [] };
    } catch (error) {
      ////console.error('🏠 Get wards error:', error);
      throw error;
    }
  },
};

export const approvalService = {
  // Lấy danh sách hóa đơn đủ điều kiện với thông tin nhóm đại lý (Cập nhật với filter mới)
  getEligibleInvoices: async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    
    // Chuẩn bị payload với các tham số filter mới
    const payload = {
      accessToken,
      limit: params.limit || 100,
      offset: params.offset || 0,
    };

    // Thêm các tham số filter nếu có giá trị
    if (params.invoiceCodeLike && params.invoiceCodeLike.trim()) {
      payload.invoiceCodeLike = params.invoiceCodeLike.trim();
    }

    if (params.statusIdFilter !== null && params.statusIdFilter !== undefined) {
      payload.statusIdFilter = params.statusIdFilter;
    }

    if (params.dailyStatusFilter !== null && params.dailyStatusFilter !== undefined) {
      payload.dailyStatusFilter = params.dailyStatusFilter;
    }

    const response = await apiClient.post('/api/auth/get-eligible-invoices-with-nhomdaily', payload);
    return response.data;
  },

  // Tính thù lao đại lý cho hóa đơn
  calculateAgentCommission: async (mahoadonxuat) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/auth/calculate-agent-commission', {
      accessToken,
      mahoadonxuat
    });
    return response.data;
  },

  // Duyệt nhân viên vào nhóm đại lý
  approveEmployee: async (IDUsers) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/auth/approve-employee', {
      accessToken,
      IDUsers
    });
    return response.data;
  },
};

// Packing Service - Quản lý đóng gói hàng
export const packingService = {
  // Lấy danh sách đơn hàng cần đóng gói
  async getPackingList(params = {}) {
    const accessToken = localStorage.getItem('accessToken');
    const requestData = {
      accessToken: accessToken,
      ...params
    };

    const response = await apiClient.post('/api/auth/get-packing-list', requestData);
    return response.data;
  },

  // Cập nhật trạng thái đóng gói
  async updatePackingStatus(invoiceId, statusId, notes = '') {
    const accessToken = localStorage.getItem('accessToken');
    const requestData = {
      accessToken: accessToken,
      invoice_id: invoiceId,
      status_id: statusId,
      notes: notes
    };

    const response = await apiClient.post('/api/auth/update-packing-status', requestData);
    return response.data;
  },

  // Lấy chi tiết đơn hàng đóng gói
  async getPackingDetail(invoiceId) {
    const accessToken = localStorage.getItem('accessToken');
    const requestData = {
      accessToken: accessToken,
      invoice_id: invoiceId
    };

    const response = await apiClient.post('/api/auth/get-packing-detail', requestData);
    return response.data;
  }
};

// Withdrawal Service - Quản lý rút tiền
export const withdrawalService = {
  // Lấy danh sách lịch sử rút tiền
  async getWithdrawalHistory(params = {}) {
    const accessToken = localStorage.getItem('accessToken');
    const requestData = {
      accessToken: accessToken,
      ...params
    };

    const response = await apiClient.post('/api/reward/withdrawal-history', requestData);
    return response.data;
  },

  // Duyệt rút tiền hàng loạt
  async approveWithdrawals(params = {}) {
    const accessToken = localStorage.getItem('accessToken');
    const requestData = {
      accessToken: accessToken,
      ...params
    };

    const response = await apiClient.post('/api/reward/approve-withdrawals', requestData);
    return response.data;
  }
};

// Referral Service - Quản lý giới thiệu
export const referralService = {
  // Lấy danh sách người giới thiệu trong tháng
  async getMonthlyReferrals(thangnam, page = 1, pageSize = 20) {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      thangnam,
      page,
      page_size: pageSize
    };

    const response = await apiClient.get('/api/gioithieu/danh-sach-thang', { params: queryParams });
    return response.data;
  }
};

// Membership Package Service - Quản lý gói thành viên
export const membershipPackageService = {
  getAll: async (params = {}) => {
    const response = await apiClient.get('/api/goithanhvien/manager', {
      params: {
        search: params.search || undefined,
        trangThai: params.trangThai || undefined,
        limit: params.limit ?? 10,
        offset: params.offset ?? 0
      }
    });
    return response.data;
  },

  create: async (packageData) => {
    const response = await apiClient.post('/api/goithanhvien/manager', packageData);
    return response.data;
  },

  update: async (id, packageData) => {
    const response = await apiClient.put(`/api/goithanhvien/manager/${id}`, packageData);
    return response.data;
  },

  updateStatus: async (id, trangThai) => {
    const response = await apiClient.patch(`/api/goithanhvien/manager/${id}/status`, {
      trangThai
    });
    return response.data;
  },

  delete: async (id) => {
    const response = await apiClient.delete(`/api/goithanhvien/manager/${id}`);
    return response.data;
  }
};

// Event Category Service - Quản lý phân loại sự kiện
export const eventCategoryService = {
  // Lấy danh sách loại sự kiện
  getAll: async () => {
    const response = await apiClient.get('/api/loaisukien');
    return response.data;
  },

  // Thêm loại sự kiện mới
  create: async (eventData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/loaisukien', eventData, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  // Cập nhật loại sự kiện
  update: async (id, eventData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/api/loaisukien/${id}`, eventData, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  // Xóa loại sự kiện
  delete: async (id) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/api/loaisukien/${id}`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`
      }
    });
    return response.data;
  }
};

// Event Service - Quản lý sự kiện
export const eventService = {
  // Lấy danh sách sự kiện
  getAll: async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const queryParams = {
      accessToken,
      ...params
    };
    const response = await apiClient.get('/api/sukien', { params: queryParams });
    return response.data;
  },

  // Lấy chi tiết sự kiện
  getById: async (id) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.get('/api/sukien', {
      params: { id, accessToken }
    });
    return response.data;
  },

  // Thêm sự kiện mới
  create: async (eventData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.post('/api/sukien', {
      accessToken,
      ...eventData
    });
    return response.data;
  },

  // Cập nhật sự kiện
  update: async (id, eventData) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/api/sukien/${id}`, {
      accessToken,
      ...eventData
    });
    return response.data;
  },

  // Cập nhật trạng thái sự kiện
  updateStatus: async (id, trangthai) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.patch(`/api/sukien/${id}/status`, {
      accessToken,
      trangthai
    });
    return response.data;
  },

  // Xóa sự kiện
  delete: async (id) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/api/sukien/${id}`, {
      data: { accessToken }
    });
    return response.data;
  }
};

// Event Registration Service - Quản lý đăng ký sự kiện
export const eventRegistrationService = {
  // Lấy danh sách đăng ký của sự kiện
  getRegistrations: async (params = {}) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.get('/api/dangkysukien/manager', {
      params,
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    return response.data;
  },

  // Cập nhật trạng thái đăng ký
  updateStatus: async (id, trangthai) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.patch(`/api/dangkysukien/manager/${id}/status`, 
      { trangthai },
      { headers: { Authorization: `Bearer ${accessToken}` }}
    );
    return response.data;
  },

  // Xóa đăng ký
  delete: async (id) => {
    const accessToken = localStorage.getItem('accessToken');
    const response = await apiClient.delete(`/api/dangkysukien/manager/${id}`, {
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    return response.data;
  }
};
